import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useVersionCheck } from '@/lib/version/useVersionCheck'
import { hasUnsavedChanges, installUnsavedChangesTracker } from '@/lib/version/unsavedChanges'
import { reloadPage } from '@/lib/player/leave'

/**
 * "There is a new version" (F-61): a card under the header, never a modal and
 * never a reload on its own. "Update" asks first when something typed would be
 * lost; "Later" hides it until the next version, or for 30 minutes when the
 * update is required.
 *
 * `z-[45]`: above the spaces sidebar (40), below the header (50), so it is seen
 * with the menu open on a phone and never covers the header's own controls.
 */
export function UpdateNotice() {
  const { t } = useTranslation('common')
  const { pending, dismiss } = useVersionCheck()
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    installUnsavedChangesTracker()
  }, [])

  /* A different notice starts over: the question was about the previous one. */
  useEffect(() => {
    setConfirming(false)
  }, [pending?.level, pending?.reason])

  if (!pending) return null

  const required = pending.level === 'required'
  const reload = () => reloadPage()
  const update = () => {
    if (!confirming && hasUnsavedChanges()) {
      setConfirming(true)
      return
    }
    reload()
  }

  const Icon = confirming || required ? AlertTriangle : Sparkles
  const title = confirming
    ? t('update.unsavedTitle')
    : required ? t('update.requiredTitle') : t('update.recommendedTitle')
  const body = confirming
    ? t('update.unsavedBody')
    : required
      ? t(pending.reason === 'chunk' ? 'update.requiredChunkBody' : 'update.requiredBody')
      : t('update.recommendedBody')

  return (
    <div
      role={required ? 'alert' : 'status'}
      aria-live={required ? 'assertive' : 'polite'}
      data-testid="update-notice"
      data-level={pending.level}
      className="pointer-events-none fixed inset-x-0 top-20 z-[45] flex justify-center px-4"
    >
      <div
        className={cn(
          'pointer-events-auto flex w-full max-w-xl flex-col gap-3 rounded-xl border bg-white p-4 shadow-lg sm:flex-row sm:items-center',
          required || confirming ? 'border-amber-200' : 'border-gray-200'
        )}
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div
            className={cn(
              'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
              required || confirming ? 'bg-amber-50' : 'bg-primary-50'
            )}
          >
            <Icon className={cn('h-4 w-4', required || confirming ? 'text-amber-600' : 'text-primary-600')} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            <p className="mt-0.5 text-sm text-gray-600">{body}</p>
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 sm:flex-none"
            onClick={() => (confirming ? setConfirming(false) : dismiss())}
          >
            {confirming ? t('update.back') : t('update.later')}
          </Button>
          <Button variant="secondary" size="sm" className="flex-1 sm:flex-none" onClick={update}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            {confirming ? t('update.refreshAnyway') : t('update.refresh')}
          </Button>
        </div>
      </div>
    </div>
  )
}
